import { readConsentState, type AnalyticsConsentState } from '../types/analytics'

export type AnalyticsOperationalCode =
  | 'consent_granted'
  | 'consent_denied'
  | 'analytics_initialized'
  | 'analytics_blocked'
  | 'analytics_init_failed'

export type AnalyticsChannel = 'ga4' | 'appinsights' | 'both'

interface AnalyticsOperationalSignal {
  code: AnalyticsOperationalCode
  channel: AnalyticsChannel
  reason?: string
}

const CONSENT_CHANGED_EVENT = 'solarheart:analytics-consent-changed'
const OPERATIONAL_EVENT = 'solarheart:analytics-operational-signal'
export const ANALYTICS_CONSENT_STORAGE_KEY = 'solarheart:analytics-consent'
const CONSENT_RECHECK_EVENTS = [
  CONSENT_CHANGED_EVENT,
  'cc:onConsent',
  'cc:onChange',
  'cookieconsent:change',
  'osano:consent-changed',
  'osano:consent-saved',
] as const

function readAnalyticsConsentStateWithStorageFallback(): AnalyticsConsentState {
  const providerState = readConsentState()
  if (providerState !== 'unavailable') {
    return providerState
  }

  if (typeof globalThis.window !== 'undefined') {
    try {
      const storedDecision = globalThis.window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY)
      if (storedDecision === 'granted') return 'granted'
      if (storedDecision === 'denied') return 'denied'
    } catch {
      // fail closed if storage is unavailable
    }
  }

  return 'unavailable'
}

export function hasAnalyticsConsent(): boolean {
  return readAnalyticsConsentStateWithStorageFallback() === 'granted'
}

export function getAnalyticsConsentBlockedReason(): string {
  const state = readAnalyticsConsentStateWithStorageFallback()
  if (state === 'granted') return 'none'
  if (state === 'denied') return 'consent_denied'
  return 'consent_provider_unavailable'
}

export function emitAnalyticsOperationalSignal(signal: AnalyticsOperationalSignal): void {
  if (typeof globalThis.window !== 'undefined') {
    globalThis.dispatchEvent(new CustomEvent(OPERATIONAL_EVENT, { detail: signal }))
  }

  if (signal.code === 'analytics_blocked' || signal.code === 'analytics_init_failed') {
    const reasonSuffix = signal.reason ? ` (${signal.reason})` : ''
    console.warn(`[Analytics] ${signal.code}:${signal.channel}${reasonSuffix}`)
  }
}

export function notifyAnalyticsConsentChanged(granted: boolean): void {
  if (typeof globalThis.window === 'undefined') return
  globalThis.dispatchEvent(
    new CustomEvent(CONSENT_CHANGED_EVENT, {
      detail: { granted },
    })
  )
}

export function persistAnalyticsConsentDecision(granted: boolean): void {
  if (typeof globalThis.window === 'undefined') return
  try {
    globalThis.window.localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, granted ? 'granted' : 'denied')
  } catch {
    // fail closed if storage is unavailable
  }
  notifyAnalyticsConsentChanged(granted)
}

export function subscribeAnalyticsConsentChanges(onChange: (granted: boolean) => void): () => void {
  if (typeof globalThis.window === 'undefined') return () => {}

  let previousState = hasAnalyticsConsent()
  const evaluate = (): void => {
    const currentState = hasAnalyticsConsent()
    if (currentState === previousState) return
    previousState = currentState
    onChange(currentState)
  }

  CONSENT_RECHECK_EVENTS.forEach((eventName) => {
    globalThis.addEventListener(eventName, evaluate)
  })

  const pollIntervalId = globalThis.setInterval(evaluate, 1000)

  return () => {
    CONSENT_RECHECK_EVENTS.forEach((eventName) => {
      globalThis.removeEventListener(eventName, evaluate)
    })
    globalThis.clearInterval(pollIntervalId)
  }
}
